import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  keyframes,
  Box,
  Divider,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { analyseSlide, patchNode } from "../../core/postNode";
import { getOnlineNode } from "../../core/getNode";
import { jsonParser, isValidJson } from "../../utils/helpers";

// Animate gradient background
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export default function AnalyseSlideWithAi({
  slide,
  slideLayout,
  csrf_token,
  presentation,
  refreshPresentation,
}) {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [slideSaving, setSlideSaving] = useState(false);
  const [slideSaveError, setSlideSaveError] = useState(false);
  const [previousResponses, setPreviousResponses] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [response, setResponse] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);

  const handleOpen = async () => {
    setOpen(true);
    setError("");
    setResponse("");

    // Fetch the latest 3 analyses for this presentation
    fetchPreviousResponses();
  };

  const handleClose = () => {
    setOpen(false);
    setError("");
    setResponse(null);
  };

  const handleAnalyse = async () => {
    setError("");
    setResponse(null);
    setLoading(true);

    try {
      const slideJson = jsonParser(slide.slide_json);
      if (!slideJson) {
        setError("Slide data is corrupted.");
        setLoading(false);
        return;
      }

      const data = await analyseSlide(
        slide.id,
        slideLayout,
        context,
        csrf_token,
      );

      if (!data || !data.response) {
        setResponse(null);
        setAnalysisId(null);
        setError("AI did not return valid analysis");
      }

      setResponse(data.response);
      setAnalysisId(data.id);

      // Refresh previous responses after new analysis
      fetchPreviousResponses();
    } catch (e) {
      setError(e.message || "Analysis failed.");
    } finally {
      setLoading(false);
    }
  };

  const acceptAiSuggestions = async (newData, responseId) => {
    setSlideSaveError(false);
    setSlideSaving(true);

    const slideData = {
      id: slide.id,
      json: newData,
      ai_edited: 1,
      presentation_id: presentation.id,
      ai_analysis_id: responseId,
    };

    const isValid = isValidJson(JSON.stringify(slideData.json));

    if (isValid) {
      patchNode("api/slide/" + slide.id, slideData, csrf_token)
        .then((response) => {
          window.postMessage({
            type: "updateSlide",
            payload: {
              id: slide.id,
              json: newData,
            },
          });

          refreshPresentation();

          setSlideSaveError(false);
          setSlideSaving(false);
          setOpen(false);
        })
        .catch((error) => {
          setSlideSaving(false);
          setSlideSaveError(true);
        });
    } else {
      setSlideSaving(false);
      setSlideSaveError(true);
    }
  };

  const restoreOriginal = async (originalData) => {
    setSlideSaveError(false);
    setSlideSaving(true);

    const json = jsonParser(originalData);

    const slideData = {
      id: slide.id,
      json: json,
      ai_edited: 0,
      presentation_id: presentation.id,
      ai_analysis_id: null,
    };

    const isValid = isValidJson(JSON.stringify(originalData));

    if (isValid) {
      patchNode("api/slide/" + slide.id, slideData, csrf_token)
        .then((response) => {
          window.postMessage({
            type: "updateSlide",
            payload: {
              id: slide.id,
              json: slideData.json,
            },
          });

          refreshPresentation();

          setSlideSaveError(false);
          setSlideSaving(false);
          setOpen(false);
        })
        .catch((error) => {
          setSlideSaving(false);
          setSlideSaveError(true);
        });
    } else {
      setSlideSaving(false);
      setSlideSaveError(true);
    }
  };

  const fetchPreviousResponses = async () => {
    setLoadingPrevious(true);

    getOnlineNode(`/api/v2/ai/slide/responses?slide_id=${slide.id}&show=3`)
      .then((response) => {
        if (response?.data?.rows?.length) {
          setPreviousResponses(response.data.rows);
        } else {
          setPreviousResponses([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching previous analyses:", error);
      })
      .finally(() => {
        setLoadingPrevious(false);
      });
  };

  return (
    <>
      {/* The main gradient button that opens the dialog */}
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<AutoAwesomeIcon />}
        sx={{
          background: "linear-gradient(270deg, #7928CA, #FF0080, #FF6B00)",
          backgroundSize: "400% 400%",
          animation: `${gradientAnimation} 6s ease infinite`,
          color: "#fff",
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "10px",
          boxShadow: "0 0 15px rgba(255, 0, 128, 0.4)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 0 25px rgba(255, 0, 128, 0.6)",
            transform: "translateY(-1px)",
          },
        }}
      />

      {error && !open && (
        <Alert sx={{ mt: 2 }} severity="error">
          {error}
        </Alert>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <Button
          sx={{ position: "absolute", top: 8, right: 0 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </Button>

        <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
          <AutoAwesomeIcon sx={{ mr: 1, color: "#FF0080" }} />
          Analyse slide with AI
        </DialogTitle>

        <DialogContent dividers>
          {/* Context input */}
          <TextField
            label="Provide additional context about this slide (optional)"
            placeholder="Providing additional context will help the AI understand the slide better and provide more accurate analysis."
            value={context}
            onChange={(e) => setContext(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert sx={{ mb: 2 }} severity="error">
              {error}
            </Alert>
          )}

          {slideSaveError && (
            <Alert sx={{ mt: 2 }} severity="error">
              Error saving slide. Please try again later.
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* New AI analysis */}
          {!loading && response && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Content analysis
              </Typography>
              <Divider sx={{ my: 1 }} />
              {response.analysis && (
                <Box
                  sx={{
                    "& section": { mb: 3 },
                    "& h2, & h3": { color: "#7928CA" },
                    "& ul": { pl: 3, mb: 2 },
                    "& li": { mb: 4 },
                    "& p": { fontSize: "0.9rem" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: response.analysis,
                  }}
                />
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Image analysis
              </Typography>
              <Divider sx={{ my: 1 }} />
              {response.image_analysis && (
                <Box
                  sx={{
                    "& section": { mb: 3 },
                    "& h2, & h3": { color: "#7928CA" },
                    "& ul": { pl: 3, mb: 2 },
                    "& li": { mb: 4 },
                    "& p": { fontSize: "0.9rem" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: response.image_analysis,
                  }}
                />
              )}

              {response.suggestions?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Suggestions
                  </Typography>
                  <Divider sx={{ my: 1 }} />

                  <Box>
                    <List dense={true}>
                      {response.suggestions.map((suggestion, index) => (
                        <ListItem key={`analysis-suggestion-${index}`}>
                          <ListItemText>- {suggestion}</ListItemText>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              )}

              {response.suggested_json && (
                <Stack direction="row" gap={2}>
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        window.open(
                          `/presentations/preview/${presentation.id}?previewChanges=true&previewChangesSlideId=${slide.id}&previewChangesAnalysisId=${analysisId}`,
                          "_blank",
                        );
                      }}
                    >
                      Preview Changes
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => {
                        acceptAiSuggestions(response.suggested_json);
                      }}
                    >
                      {slideSaving ? (
                        <>
                          <CircularProgress
                            size={20}
                            color="white"
                            sx={{ position: "absolute" }}
                          />
                          <span style={{ visibility: "hidden" }}>
                            Accept Changes
                          </span>
                        </>
                      ) : (
                        "Accept Changes"
                      )}
                    </Button>
                  </Box>
                </Stack>
              )}
            </>
          )}

          <Stack direction="row" gap={2} justifyContent="end">
            <Button
              onClick={handleAnalyse}
              variant="contained"
              disabled={loading}
              startIcon={!loading && <AutoAwesomeIcon />}
              sx={{
                background:
                  "linear-gradient(270deg, #7928CA, #FF0080, #FF6B00)",
                backgroundSize: "400% 400%",
                animation: `${gradientAnimation} 6s ease infinite`,
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                boxShadow: "0 0 10px rgba(255, 0, 128, 0.3)",
                "&:hover": { boxShadow: "0 0 20px rgba(255, 0, 128, 0.5)" },
                "&:disabled": { opacity: 0.6 },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Analyse"
              )}
            </Button>
          </Stack>

          {/* Previous 3 analyses */}
          {!loadingPrevious && previousResponses.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Previous Analyses
              </Typography>
              {previousResponses.map((previousResponse) => {
                try {
                  const parsed = jsonParser(previousResponse.response);

                  return (
                    <Accordion key={previousResponse.id}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {previousResponse.created}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <>
                          {parsed.analysis && (
                            <>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Content analysis
                              </Typography>
                              <Divider sx={{ my: 1 }} />
                              <Box
                                sx={{
                                  "& section": { mb: 3 },
                                  "& h2, & h3": { color: "#7928CA" },
                                  "& ul": { pl: 3, mb: 2 },
                                  "& li": { mb: 4 },
                                  "& p": { fontSize: "0.9rem" },
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: parsed.analysis,
                                }}
                              />
                            </>
                          )}

                          {parsed.image_analysis && (
                            <>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Image analysis
                              </Typography>
                              <Divider sx={{ my: 1 }} />
                              <Box
                                sx={{
                                  "& section": { mb: 3 },
                                  "& h2, & h3": { color: "#7928CA" },
                                  "& ul": { pl: 3, mb: 2 },
                                  "& li": { mb: 4 },
                                  "& p": { fontSize: "0.9rem" },
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: parsed.image_analysis,
                                }}
                              />
                            </>
                          )}

                          {parsed.suggestions?.length > 0 && (
                            <>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Suggestions
                              </Typography>
                              <Divider sx={{ my: 1 }} />
                              <Box>
                                <List dense={true}>
                                  {parsed.suggestions.map(
                                    (suggestion, index) => (
                                      <ListItem
                                        key={`analysis-${previousResponse.id}-suggestion-${index}`}
                                      >
                                        <ListItemText>
                                          - {suggestion}
                                        </ListItemText>
                                      </ListItem>
                                    ),
                                  )}
                                </List>
                              </Box>
                            </>
                          )}

                          {parsed.suggested_json && (
                            <Stack direction="row" gap={2}>
                              {slide.ai_analysis_id === previousResponse.id ? (
                                <Box>
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      restoreOriginal(
                                        previousResponse.original_slide_json,
                                      );
                                    }}
                                  >
                                    Restore original
                                  </Button>
                                </Box>
                              ) : (
                                <>
                                  <Box>
                                    <Button
                                      variant="outlined"
                                      onClick={() => {
                                        window.open(
                                          `/presentations/preview/${presentation.id}?previewChanges=true&previewChangesSlideId=${slide.id}&previewChangesAnalysisId=${previousResponse.id}`,
                                          "_blank",
                                        );
                                      }}
                                    >
                                      Preview Changes
                                    </Button>
                                  </Box>
                                  <Box>
                                    <Button
                                      variant="contained"
                                      onClick={() => {
                                        acceptAiSuggestions(
                                          parsed.suggested_json,
                                          previousResponse.id,
                                        );
                                      }}
                                    >
                                      {slideSaving ? (
                                        <>
                                          <CircularProgress
                                            size={20}
                                            color="white"
                                            sx={{ position: "absolute" }}
                                          />
                                          <span
                                            style={{ visibility: "hidden" }}
                                          >
                                            Accept Changes
                                          </span>
                                        </>
                                      ) : (
                                        "Accept Changes"
                                      )}
                                    </Button>
                                  </Box>
                                </>
                              )}
                            </Stack>
                          )}
                        </>
                      </AccordionDetails>
                    </Accordion>
                  );
                } catch (e) {
                  console.error(e);
                  return null;
                }
              })}
            </>
          )}

          {/* Loading state for previous analyses */}
          {loadingPrevious && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>

        {/* <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button
            onClick={handleAnalyse}
            variant="contained"
            disabled={loading}
            startIcon={!loading && <AutoAwesomeIcon />}
            sx={{
              background: "linear-gradient(270deg, #7928CA, #FF0080, #FF6B00)",
              backgroundSize: "400% 400%",
              animation: `${gradientAnimation} 6s ease infinite`,
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              boxShadow: "0 0 10px rgba(255, 0, 128, 0.3)",
              "&:hover": { boxShadow: "0 0 20px rgba(255, 0, 128, 0.5)" },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Analyse"
            )}
          </Button>
        </DialogActions> */}
      </Dialog>
    </>
  );
}
